import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { api, gbp2 } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";

export default function Consent() {
  const { bid } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [answers, setAnswers] = useState({}); // { treatment_id: { qKey: {answer, comment} } }
  const [signatures, setSignatures] = useState({}); // { treatment_id: dataURL }
  const padRefs = useRef({}); // { treatment_id: SignaturePad ref }
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [refusalNotified, setRefusalNotified] = useState(false);

  useEffect(() => {
    api.get(`/consent-forms/for-booking/${bid}`, { params: { token } })
      .then((r) => setState({ loading: false, error: null, data: r.data }))
      .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "Link invalid", data: null }));
  }, [bid, token]);

  const setAns = (tid, qKey, patch) => {
    setAnswers((prev) => ({ ...prev, [tid]: { ...(prev[tid] || {}), [qKey]: { ...((prev[tid] || {})[qKey] || {}), ...patch } } }));
  };

  const isMulti = state.data && state.data.forms_needed.length > 1;
  // When the client has more than one treatment, merge the unique questions
  // (deduped by key) from every selected template so we can show ONE combined
  // "GENERAL MEDICAL QUESTIONS" section instead of asking the same medical
  // history over and over. The first form's treatment_id acts as the shared
  // answer bucket; on submit we replicate that bucket into every form.
  const combined = useMemo(() => {
    if (!state.data || state.data.forms_needed.length <= 1) return null;
    const primaryTid = state.data.forms_needed[0].treatment_id;
    const seen = new Set();
    const questions = [];
    for (const f of state.data.forms_needed) {
      for (const q of f.template.questions) {
        if (seen.has(q.key)) continue;
        seen.add(q.key);
        questions.push(q);
      }
    }
    return { primaryTid, questions };
  }, [state.data]);

  // Refusal detection: yes to refuse_on_yes OR no to require_yes
  const refusals = useMemo(() => {
    if (!state.data) return [];
    const list = [];
    if (combined) {
      const a = answers[combined.primaryTid] || {};
      for (const q of combined.questions) {
        const val = a[q.key] || {};
        const ans = (val.answer || "").toLowerCase();
        if (q.refuse_on_yes && ans === "yes") {
          list.push({ treatment_id: combined.primaryTid, treatment_name: "Combined form", key: q.key, text: q.text, answer: "yes", note: q.refuse_note || "" });
        } else if (q.require_yes && ans === "no") {
          list.push({ treatment_id: combined.primaryTid, treatment_name: "Combined form", key: q.key, text: q.text, answer: "no", note: "Client did not confirm understanding." });
        }
      }
      return list;
    }
    for (const f of state.data.forms_needed) {
      const a = answers[f.treatment_id] || {};
      for (const q of f.template.questions) {
        const val = a[q.key] || {};
        const ans = (val.answer || "").toLowerCase();
        if (q.refuse_on_yes && ans === "yes") {
          list.push({ treatment_id: f.treatment_id, treatment_name: f.treatment_name, key: q.key, text: q.text, answer: "yes", note: q.refuse_note || "" });
        } else if (q.require_yes && ans === "no") {
          list.push({ treatment_id: f.treatment_id, treatment_name: f.treatment_name, key: q.key, text: q.text, answer: "no", note: "Client did not confirm understanding." });
        }
      }
    }
    return list;
  }, [state.data, answers, combined]);

  const missing = useMemo(() => {
    if (!state.data) return [];
    const list = [];
    if (combined) {
      const a = answers[combined.primaryTid] || {};
      const unanswered = [];
      for (const q of combined.questions) {
        if (!q.required) continue;
        const val = a[q.key] || {};
        if (q.kind === "comment_only") {
          if (!(val.comment || "").trim()) unanswered.push(q);
        } else if (!val.answer) {
          unanswered.push(q);
        }
      }
      if (unanswered.length > 0) {
        list.push({
          label: `${unanswered.length} question${unanswered.length === 1 ? "" : "s"} still to answer`,
          target: `q-combined-${unanswered[0].key}`,
        });
      }
      if (!signatures[combined.primaryTid]) {
        list.push({ label: "Please sign in the box", target: "sig-block-combined" });
      }
      return list;
    }
    for (const f of state.data.forms_needed) {
      const a = answers[f.treatment_id] || {};
      const unanswered = [];
      for (const q of f.template.questions) {
        if (!q.required) continue;
        const val = a[q.key] || {};
        if (q.kind === "comment_only") {
          if (!(val.comment || "").trim()) unanswered.push(q);
        } else if (!val.answer) {
          unanswered.push(q);
        }
      }
      if (unanswered.length > 0) {
        list.push({
          label: `${f.treatment_name}: ${unanswered.length} question${unanswered.length === 1 ? "" : "s"} to answer`,
          target: `q-${f.treatment_id}-${unanswered[0].key}`,
        });
      }
      if (!signatures[f.treatment_id]) {
        list.push({
          label: `${f.treatment_name}: please sign in the box`,
          target: `sig-block-${f.treatment_id}`,
        });
      }
    }
    return list;
  }, [state.data, answers, signatures, combined]);

  const scrollToTarget = (target) => {
    if (!target) return;
    const el = document.querySelector(`[data-testid="${target}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-amber-400", "ring-offset-2");
    setTimeout(() => el.classList.remove("ring-2", "ring-amber-400", "ring-offset-2"), 1600);
  };

  const buildForms = () => state.data.forms_needed.map((f) => {
    // In multi-treatment mode every form receives the SAME combined answers
    // and signature (drawn from the primary bucket). Answers whose question
    // key doesn't exist in this specific form's template are simply dropped
    // by the .map below — the backend only stores keys it recognises.
    const bucketTid = combined ? combined.primaryTid : f.treatment_id;
    return {
      treatment_id: f.treatment_id,
      template_key: f.template_key,
      signature_name: state.data.booking.client_name,
      signature_image: signatures[bucketTid] || "",
      answers: f.template.questions.map((q) => ({
        key: q.key,
        answer: (answers[bucketTid]?.[q.key]?.answer) || "",
        comment: (answers[bucketTid]?.[q.key]?.comment) || "",
      })),
    };
  });

  const submit = async () => {
    if (refusals.length > 0) {
      toast.error("Please use 'Notify Clinic' to submit your answers.");
      return;
    }
    if (missing.length > 0) {
      toast.error(missing[0].label);
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/consent-forms/for-booking/${bid}?token=${encodeURIComponent(token)}`, { forms: buildForms() });
      setDone(true);
      toast.success("Consent submitted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    }
    setSubmitting(false);
  };

  const notifyClinic = async () => {
    setSubmitting(true);
    try {
      await api.post(`/consent-forms/for-booking/${bid}/refusal?token=${encodeURIComponent(token)}`, { forms: buildForms() });
      setRefusalNotified(true);
      toast.success("The clinic has been notified. We'll be in touch shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not notify the clinic. Please contact us directly.");
    }
    setSubmitting(false);
  };

  if (state.loading) return <main className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><p className="mono-label">Loading your consent…</p></main>;
  if (state.error) return <main className="min-h-screen flex items-center justify-center bg-[#FAF9F6] p-8 text-center"><div><h1 className="font-serif text-3xl">Link no longer valid</h1><p className="mt-3 text-stone-600">{state.error}</p></div></main>;
  const { booking, forms_needed, already_submitted } = state.data;

  if (done || (forms_needed.length === 0 && already_submitted.length > 0)) {
    return (
      <main className="min-h-screen bg-[#FAF9F6] px-6 py-16">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-800 px-3 py-1 rounded-sm border border-amber-200"><Check size={16} /><span className="mono-label">Submitted · Awaiting Review</span></div>
          <h1 className="mt-6 font-serif text-4xl font-light text-stone-900">Thank you</h1>
          <p className="mt-3 text-stone-600">Your consent has been submitted securely to CLAÈ. Our practitioner will review your responses before your appointment.</p>
          <p className="mt-8 text-sm text-stone-500">Booking <span className="font-mono">{booking.reference}</span> · {booking.date} at {booking.time}</p>
        </div>
      </main>
    );
  }

  if (refusalNotified) {
    return (
      <main className="min-h-screen bg-[#FAF9F6] px-6 py-16" data-testid="refusal-notified-screen">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-800 px-3 py-1 rounded-sm border border-amber-200"><AlertTriangle size={16} /><span className="mono-label">Clinic Notified</span></div>
          <h1 className="mt-6 font-serif text-4xl font-light text-stone-900">Thank you for sharing</h1>
          <p className="mt-3 text-stone-600">Based on your answers we are unable to confirm your treatment at this time. A member of our team will be in touch to discuss next steps, and where appropriate, request medical clearance from your GP or dermatologist.</p>
          <p className="mt-8 text-sm text-stone-500">Booking <span className="font-mono">{booking.reference}</span> · {booking.date} at {booking.time}</p>
        </div>
      </main>
    );
  }

  const hasRefusals = refusals.length > 0;

  return (
    <main className="min-h-screen bg-[#FAF9F6] pb-24">
      <header className="border-b hairline bg-white">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 py-4">
          <span className="font-serif text-2xl">CLAÈ</span>
          <span className="mono-label">Treatment Consent</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 pt-10 fade-in">
        <p className="mono-label">Booking {booking.reference} · {booking.date} · {booking.time}</p>
        <h1 className="font-serif text-4xl font-light text-stone-900 mt-2">Hi {booking.client_name.split(" ")[0]}, please complete your consent</h1>
        <p className="mt-3 text-stone-600 text-sm max-w-xl">Read the disclosure, answer every question honestly, and sign at the bottom. This takes about two minutes.</p>

        {isMulti ? (
          /* ══════════ Multi-treatment: combined form ══════════
             Each treatment's disclaimer stacks as its own card (heading +
             existing 4-line clamp + existing "Read more" button), followed
             by ONE combined questionnaire (duplicate-keyed questions merged),
             then ONE signature block. */
          <>
            {forms_needed.map((f) => {
              const displayName = ({
                "anti-wrinkle": "Anti-Wrinkle",
                "dermal-filler": "Dermal Filler",
                "polynucleotide": "Polynucleotides",
                "profhilo": "Profhilo",
                "lumi-eyes": "Lumi Eyes",
                "microneedling": "Microneedling",
                "b12": "Vitamin B12",
                "hyalase": "Hyalase (Lip Dissolving)",
                "pixie-tip-lift": "Pixie Tip Lift",
              })[f.template_key] || f.treatment_name;
              return (
                <section
                  key={f.treatment_id}
                  className="mt-10 border hairline bg-white p-6 sm:p-8"
                  data-testid={`treatment-info-${f.treatment_id}`}
                >
                  <h2 className="font-serif text-3xl font-light text-stone-900">{displayName}</h2>
                  <DisclosureBlock text={f.template.intro} testid={`disclosure-${f.treatment_id}`} />
                </section>
              );
            })}

            <section className="mt-12 border hairline bg-white p-6 sm:p-8" data-testid="combined-medical-questions">
              <p className="mono-label">General Medical Questions</p>
              <h2 className="font-serif text-3xl font-light text-stone-900 mt-1">A Few Questions About You</h2>
              <p className="text-sm text-stone-600 mt-2">These apply to every treatment above. Please answer honestly — your safety comes first.</p>

              <div className="mt-8 space-y-6">
                {combined.questions.map((q) => {
                  const val = answers[combined.primaryTid]?.[q.key] || {};
                  const commentVisible = q.kind === "comment_only" || (q.kind === "yesno_comment" && val.answer === "yes");
                  const isTriggered = (q.refuse_on_yes && val.answer === "yes") || (q.require_yes && val.answer === "no");
                  return (
                    <div key={q.key} className={`border-t hairline pt-5 ${isTriggered ? "border-l-2 border-l-amber-400 pl-4 -ml-4" : ""}`} data-testid={`q-combined-${q.key}`}>
                      <p className="text-sm text-stone-900 leading-relaxed">{q.text}</p>
                      {q.kind !== "comment_only" && (
                        <div className="mt-3 flex gap-2">
                          {["yes", "no"].map((choice) => (
                            <button
                              key={choice}
                              type="button"
                              data-testid={`ans-combined-${q.key}-${choice}`}
                              onClick={() => setAns(combined.primaryTid, q.key, { answer: choice })}
                              className={`mono-label px-6 py-2 border transition ${val.answer === choice ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-600 hover:border-stone-900"}`}
                              style={val.answer === choice ? { color: "#fff" } : {}}
                            >
                              {choice.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      )}
                      {isTriggered && (
                        <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2" data-testid={`refuse-note-combined-${q.key}`}>
                          {q.refuse_note || "Based on this answer, we're unable to proceed with treatment. Please use 'Notify Clinic' below so we can review your case."}
                        </p>
                      )}
                      {commentVisible && (
                        <textarea
                          data-testid={`comment-combined-${q.key}`}
                          value={val.comment || ""}
                          onChange={(e) => setAns(combined.primaryTid, q.key, { comment: e.target.value })}
                          placeholder="Please provide details"
                          rows={3}
                          className="mt-3 w-full border hairline p-3 text-sm focus:outline-none focus:border-stone-900"
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {!hasRefusals && (
                <div className="mt-8 border-t hairline pt-6" data-testid="sig-block-combined">
                  <div className="flex items-baseline justify-between">
                    <label className="mono-label">Client Signature</label>
                    <button
                      type="button"
                      data-testid="sig-clear-combined"
                      onClick={() => { padRefs.current[combined.primaryTid]?.clear(); }}
                      className="mono-label text-stone-500 hover:text-stone-900"
                    >Clear</button>
                  </div>
                  <p className="text-xs text-stone-500 mt-1">Sign with your finger, stylus or trackpad. This signature covers every treatment listed above.</p>
                  <div className="mt-3">
                    <SignaturePad
                      ref={(el) => { padRefs.current[combined.primaryTid] = el; }}
                      height={170}
                      onChange={(dataUrl) => setSignatures((s) => ({ ...s, [combined.primaryTid]: dataUrl }))}
                    />
                  </div>
                  <p className="mono-label text-stone-400 mt-2">
                    {signatures[combined.primaryTid] ? "Signed" : "Awaiting signature"} · {format(new Date(), "d MMM yyyy")} · {booking.client_name}
                  </p>
                </div>
              )}
            </section>
          </>
        ) : (
          forms_needed.map((f, idx) => {
          const displayName = ({
            "anti-wrinkle": "Anti-Wrinkle",
            "dermal-filler": "Dermal Filler",
            "polynucleotide": "Polynucleotides",
            "profhilo": "Profhilo",
            "lumi-eyes": "Lumi Eyes",
            "microneedling": "Microneedling",
            "b12": "Vitamin B12",
          })[f.template_key] || f.treatment_name;
          // Derive the "treatment area" suffix from the booked treatment name
          // (e.g. "Microneedling – Face + Neck" → "Face + Neck") so multi-area
          // treatments record which area the client is consenting to.
          const areaMatch = (f.treatment_name || "").match(/[–\-]\s*(.+)$/);
          const treatmentArea = areaMatch ? areaMatch[1].trim() : "";
          return (
          <section key={f.treatment_id} className="mt-12 border hairline bg-white p-6 sm:p-8" data-testid={`form-${f.treatment_id}`}>
            <p className="mono-label">Form {idx + 1} of {forms_needed.length}</p>
            <h2 className="font-serif text-3xl font-light text-stone-900 mt-1">{displayName}</h2>
            <p className="mono-label mt-1">{f.template.title}</p>
            {treatmentArea && (
              <p
                className="mono-label mt-2 text-stone-700"
                data-testid={`treatment-area-${f.treatment_id}`}
              >
                TREATMENT AREA · {treatmentArea.toUpperCase()}
              </p>
            )}

            <div className="mt-6 border-t hairline pt-6">
              <DisclosureBlock text={f.template.intro} testid={`disclosure-${f.treatment_id}`} />
            </div>

            <div className="mt-8 space-y-6">
              {f.template.questions.map((q) => {
                const val = answers[f.treatment_id]?.[q.key] || {};
                const commentVisible = q.kind === "comment_only" || (q.kind === "yesno_comment" && val.answer === "yes");
                const isTriggered = (q.refuse_on_yes && val.answer === "yes") || (q.require_yes && val.answer === "no");
                return (
                  <div key={q.key} className={`border-t hairline pt-5 ${isTriggered ? "border-l-2 border-l-amber-400 pl-4 -ml-4" : ""}`} data-testid={`q-${f.treatment_id}-${q.key}`}>
                    <p className="text-sm text-stone-900 leading-relaxed">{q.text}</p>
                    {q.kind !== "comment_only" && (
                      <div className="mt-3 flex gap-2">
                        {["yes", "no"].map((choice) => (
                          <button
                            key={choice}
                            type="button"
                            data-testid={`ans-${f.treatment_id}-${q.key}-${choice}`}
                            onClick={() => setAns(f.treatment_id, q.key, { answer: choice })}
                            className={`mono-label px-6 py-2 border transition ${val.answer === choice ? (choice === "yes" ? "bg-stone-900 text-white border-stone-900" : "bg-stone-900 text-white border-stone-900") : "border-stone-300 text-stone-600 hover:border-stone-900"}`}
                            style={val.answer === choice ? { color: "#fff" } : {}}
                          >
                            {choice.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    )}
                    {isTriggered && (
                      <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2" data-testid={`refuse-note-${f.treatment_id}-${q.key}`}>
                        {q.refuse_note || "Based on this answer, we're unable to proceed with treatment. Please use 'Notify Clinic' below so we can review your case."}
                      </p>
                    )}
                    {commentVisible && (
                      <textarea
                        data-testid={`comment-${f.treatment_id}-${q.key}`}
                        value={val.comment || ""}
                        onChange={(e) => setAns(f.treatment_id, q.key, { comment: e.target.value })}
                        placeholder="Please provide details"
                        rows={3}
                        className="mt-3 w-full border hairline p-3 text-sm focus:outline-none focus:border-stone-900"
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {!hasRefusals && (
              <div className="mt-8 border-t hairline pt-6" data-testid={`sig-block-${f.treatment_id}`}>
                <div className="flex items-baseline justify-between">
                  <label className="mono-label">Client Signature</label>
                  <button
                    type="button"
                    data-testid={`sig-clear-${f.treatment_id}`}
                    onClick={() => { padRefs.current[f.treatment_id]?.clear(); }}
                    className="mono-label text-stone-500 hover:text-stone-900"
                  >Clear</button>
                </div>
                <p className="text-xs text-stone-500 mt-1">Sign with your finger, stylus or trackpad. This is the signature of the person having the treatment.</p>
                <div className="mt-3">
                  <SignaturePad
                    ref={(el) => { padRefs.current[f.treatment_id] = el; }}
                    height={170}
                    onChange={(dataUrl) => setSignatures((s) => ({ ...s, [f.treatment_id]: dataUrl }))}
                  />
                </div>
                <p className="mono-label text-stone-400 mt-2">
                  {signatures[f.treatment_id] ? "Signed" : "Awaiting signature"} · {format(new Date(), "d MMM yyyy")} · {booking.client_name}
                </p>
              </div>
            )}
          </section>
          );
        }))}

        {hasRefusals && (
          <div className="mt-10 border border-amber-300 bg-amber-50 p-6" data-testid="refusal-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-700 shrink-0 mt-0.5" />
              <div>
                <p className="mono-label text-amber-900">We're kindly unable to proceed with treatment</p>
                <p className="mt-2 text-sm text-stone-800 leading-relaxed">
                  Based on your answers, your safety comes first and we can't confirm this treatment today. Please tap <strong>Notify Clinic</strong> below so we can review your case with you personally. Where appropriate, we'll ask you to provide medical clearance from your GP or dermatologist before rebooking. Your deposit will be discussed with you directly.
                </p>
                <ul className="mt-3 text-xs text-stone-700 space-y-1">
                  {refusals.map((r, i) => (
                    <li key={i}>• <strong>{r.treatment_name}</strong>: {r.text}{r.note ? ` — ${r.note}` : ""}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {!hasRefusals && (
          <div className="mt-10 border hairline bg-white p-6">
            <p className="mono-label">Payment Summary</p>
            <div className="mt-4 space-y-1 text-sm">
              <div className="flex justify-between text-stone-600"><span>Subtotal</span><span>{gbp2(booking.subtotal)}</span></div>
              <div className="flex justify-between text-stone-600"><span>Discount</span><span>{gbp2(booking.subtotal - booking.final_total)}</span></div>
              <div className="flex justify-between pt-2 border-t hairline font-medium"><span>Total</span><span className="font-serif text-2xl">{gbp2(booking.final_total)}</span></div>
              <div className="flex justify-between text-sm text-stone-600 pt-2"><span>Deposit paid</span><span>{gbp2(booking.amount_paid)}</span></div>
              <div className="flex justify-between text-sm"><span>Balance remaining</span><span>{gbp2(booking.remaining_balance)}</span></div>
            </div>
          </div>
        )}

        {hasRefusals ? (
          <button
            data-testid="notify-clinic"
            disabled={submitting}
            onClick={notifyClinic}
            className="mt-10 w-full bg-amber-700 text-white py-4 mono-label disabled:opacity-30 hover:bg-amber-800 transition"
            style={{ color: "#fff" }}
          >
            {submitting ? "Notifying…" : "Notify Clinic"}
          </button>
        ) : (
          <button
            data-testid="submit-consent"
            disabled={submitting}
            onClick={submit}
            className="mt-10 w-full bg-stone-900 text-white py-4 mono-label disabled:opacity-30 hover:bg-[#C5A059] transition"
            style={{ color: "#fff" }}
          >
            {submitting ? "Submitting…" : missing.length > 0 ? `Submit Consent (${missing.length} still to complete)` : "Submit Consent"}
          </button>
        )}
        {!hasRefusals && missing.length > 0 && (
          <div className="mt-3 text-center text-xs text-stone-600" data-testid="missing-summary">
            <p className="mb-1">Please complete before submitting:</p>
            <ul className="space-y-1">
              {missing.map((m, i) => (
                <li key={i}>
                  <button
                    type="button"
                    data-testid={`missing-link-${i}`}
                    onClick={() => scrollToTarget(m.target)}
                    className="underline decoration-dotted underline-offset-2 hover:text-stone-900 hover:decoration-solid transition"
                  >
                    • {m.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </main>
  );
}


/**
 * DisclosureBlock — collapsed preview of the consent disclosure. Shows the
 * first 4 lines by default with a soft fade-out, expands on "Read more" to
 * reveal the full body of the statement.
 */
function DisclosureBlock({ text, testid }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mt-4" data-testid={testid}>
      <div
        className={`relative text-sm text-stone-700 leading-relaxed whitespace-pre-line transition-all ${
          open ? "" : "line-clamp-4"
        }`}
        style={
          open
            ? {}
            : {
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
        }
      >
        {text}
        {!open && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent" />
        )}
      </div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-toggle`}
        className="mt-2 mono-label text-stone-700 hover:text-stone-900 underline decoration-dotted underline-offset-4"
      >
        {open ? "Show less" : "Read more"}
      </button>
    </div>
  );
}
